import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";

import {
  GCP_WORFKLOW_EXECUTION_STATUS_LABELS,
  GCP_WORKFLOW_EXECUTION_STATUSES,
  GCP_WORKFLOW_EXECUTION_UI_TABLE_COLUMNS,
} from "constants";

const formatDurationString = (durationStr) => {
    const dayTimeParts = durationStr.split(' ');

    let days = 0;
    let timePart = "";

    if (dayTimeParts.length === 2) {
        days = parseInt(dayTimeParts[0]);
        timePart = dayTimeParts[1];
    } else {
        timePart = dayTimeParts[0];
    }

    // Split time into hours, minutes, seconds
    const [hoursStr, minutesStr, secondsFractionStr] = timePart.split(':');

    const hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    // Separate whole seconds from microseconds
    const [secondsStr, _] = secondsFractionStr.split('.');
    const seconds = parseInt(secondsStr);

    // Build a human-readable string
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds) parts.push(`${seconds}s`);

    return parts.length > 0 ? parts[0] : '-';
};

const generateBigQueryLinkFromExecutionResult = (result) => {
  const { tableReference } = result;
  if (!tableReference) {
    return "";
  }
  const { projectId, datasetId, tableId } = tableReference;
  return `https://console.cloud.google.com/bigquery?project=${projectId}&ws=!1m5!1m4!4m3!1s${projectId}!2s${datasetId}!3s${tableId}`;
}


const DialogCloseButton = ({ onClose }) => {
  DialogCloseButton.propTypes = {
    onClose: PropTypes.func.isRequired,
  };

  return (
    <IconButton
      aria-label="Close dialog"
      onClick={onClose}
      sx={{
        position: "absolute",
        right: 12,
        top: 12,
        color: "grey.600",
      }}
    >
      <CloseIcon />
    </IconButton>
  );
};

export default function GcpWorkflowExecutionsTable({
  executions,
  optionalVisibleColumns = [],
}) {
  GcpWorkflowExecutionsTable.propTypes = {
    executions: PropTypes.array.isRequired,
    optionalVisibleColumns: PropTypes.array,
  };

  const [
    idOfExecutionWithErrorDialogVisible,
    setIdOfExecutionWithErrorDialogVisible,
  ] = useState(null);
  const [
    idOfExecutionWithDetailsDialogVisible,
    setIdOfExecutionWithDetailsDialogVisible,
  ] = useState(null);

  const isVendorFieldVisible = optionalVisibleColumns.includes(
    GCP_WORKFLOW_EXECUTION_UI_TABLE_COLUMNS.VENDOR,
  ) ? true : false;
  

  const statusBadgeColorByState = {
    [GCP_WORKFLOW_EXECUTION_STATUSES.ACTIVE]: "secondary",
    [GCP_WORKFLOW_EXECUTION_STATUSES.SUCCEEDED]: "success",
    [GCP_WORKFLOW_EXECUTION_STATUSES.FAILED]: "error",
  };

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }} square>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Job Name</TableCell>
            {isVendorFieldVisible && <TableCell>Vendor</TableCell>}
            <TableCell>ID</TableCell>
            <TableCell>Date Started</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {executions.map(
            ({
              state,
              job_id,
              job_name,
              start_time,
              end_time,
              duration,
              vendor_name,
              result,
              error,
              arguments: _arguments // Avoid using 'arguments' as it's a reserved keyword in JavaScript
            }) => (
              <TableRow key={job_id}>
                <TableCell sx={{ maxWidth: "400px" }}>
                  {job_name ?? "-"}
                </TableCell>
                {isVendorFieldVisible && (
                  <TableCell>
                    {vendor_name ?? "-"}
                  </TableCell>
                )}
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {job_id}
                </TableCell>
                <TableCell>
                  {new Date(start_time).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {duration ? formatDurationString(duration) : '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {
                    <Chip
                      variant="outlined"
                      label={
                        GCP_WORFKLOW_EXECUTION_STATUS_LABELS[state] ?? "Unknown"
                      }
                      color={statusBadgeColorByState[state] ?? "default"}
                    />
                  }
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {state === GCP_WORKFLOW_EXECUTION_STATUSES.SUCCEEDED &&
                      result && (
                        <Button
                          component={Link}
                          href={generateBigQueryLinkFromExecutionResult(result)}
                          target="_blank"
                          sx={{
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          View results in BigQuery{" "}
                          <LaunchIcon sx={{ ml: 1, fontSize: "16px" }} />
                        </Button>
                      )}
                    {state === GCP_WORKFLOW_EXECUTION_STATUSES.FAILED && (
                      <>
                        <Button
                          sx={{ fontWeight: 600 }}
                          onClick={() =>
                            setIdOfExecutionWithErrorDialogVisible(
                              job_id,
                            )
                          }
                        >
                          View Error Details
                        </Button>
                        <Dialog
                          onClose={() =>
                            setIdOfExecutionWithErrorDialogVisible(null)
                          }
                          open={
                            job_id ===
                            idOfExecutionWithErrorDialogVisible
                          }
                          sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: '80%' } }}
                        >
                          <DialogTitle>Error Message</DialogTitle>
                          <DialogCloseButton
                            onClose={() =>
                              setIdOfExecutionWithErrorDialogVisible(null)
                            }
                          />
                          <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                            <Box sx={{ backgroundColor: "grey.300", p: 2 }}>
                              {error.context && (
                                <Typography
                                  component="pre"
                                  sx={{ wordWrap: "break-word" }}
                                >
                                  {JSON.stringify(error.context, null, 2)}
                                </Typography>
                              )}
                            </Box>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                    <>
                      <Button
                        sx={{ fontWeight: 600 }}
                        onClick={() =>
                          setIdOfExecutionWithDetailsDialogVisible(
                            job_id,
                          )
                        }
                      >
                        View Job Details
                      </Button>
                      <Dialog
                        onClose={() =>
                          setIdOfExecutionWithDetailsDialogVisible(null)
                        }
                        open={
                          job_id ===
                          idOfExecutionWithDetailsDialogVisible
                        }
                      >
                        <DialogTitle>Job Details </DialogTitle>
                        <DialogCloseButton
                          onClose={() =>
                            setIdOfExecutionWithDetailsDialogVisible(null)
                          }
                        />
                        <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                          <Box sx={{ backgroundColor: "grey.300", p: 2 }}>
                              <Typography
                                component="pre"
                                sx={{ wordWrap: "break-word" }}
                              >
                                {JSON.stringify(_arguments, null, 2)}
                              </Typography>
                          </Box>
                        </DialogContent>
                      </Dialog>
                    </>
                  </Box>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
